import { useState, type FormEvent } from "react";
import { useActiveTenant } from "../tenant/TenantContext";
import { useTenantMutation, useTenantQuery } from "../hooks/useTenantData";
import { can } from "../tenant/permissions";
import type { TenantContact, TenantProfile, TenantProfilePatch } from "../api/types";
import { Field, InlineError, PageHeader, Panel, QueryState } from "../components/ui";

const TIMEZONES = [
  "America/Sao_Paulo",
  "America/Manaus",
  "America/Cuiaba",
  "America/Fortaleza",
  "America/Recife",
  "America/Rio_Branco",
];

export function ProfilePage() {
  const membership = useActiveTenant();
  const canEdit = can(membership.role, "profile.edit");
  const profile = useTenantQuery(["profile"], (api, t) => api.getProfile(t));

  return (
    <>
      <PageHeader title="Perfil do condomínio" subtitle="Dados cadastrais, endereço, fuso e contatos." />
      <QueryState query={profile}>
        {(data) => <ProfileForm key={data.tenant_id} profile={data} canEdit={canEdit} />}
      </QueryState>
    </>
  );
}

function ProfileForm({ profile, canEdit }: { profile: TenantProfile; canEdit: boolean }) {
  const [contacts, setContacts] = useState<TenantContact[]>(profile.contacts);
  const [saved, setSaved] = useState(false);
  const update = useTenantMutation<TenantProfilePatch>(
    (api, t, patch) => api.updateProfile(t, patch),
    [["profile"]],
  );

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaved(false);
    const data = new FormData(event.currentTarget);
    const patch: TenantProfilePatch = {
      name: String(data.get("name") || profile.name),
      document: String(data.get("document") || "") || null,
      address: String(data.get("address") || "") || null,
      city: String(data.get("city") || "") || null,
      state: String(data.get("state") || "") || null,
      postal_code: String(data.get("postal_code") || "") || null,
      timezone: String(data.get("timezone") || profile.timezone),
      contacts,
    };
    update.mutate(patch, { onSuccess: () => setSaved(true) });
  }

  return (
    <form onSubmit={submit}>
      <Panel title="Dados cadastrais">
        <fieldset disabled={!canEdit} style={{ border: 0, padding: 0, margin: 0 }}>
          <div className="form-grid">
            <Field label="Nome do condomínio" name="name" required defaultValue={profile.name} />
            <Field label="CNPJ" name="document" defaultValue={profile.document ?? ""} />
            <Field label="Endereço" name="address" defaultValue={profile.address ?? ""} />
            <Field label="Cidade" name="city" defaultValue={profile.city ?? ""} />
            <Field label="UF" name="state" defaultValue={profile.state ?? ""} />
            <Field label="CEP" name="postal_code" defaultValue={profile.postal_code ?? ""} />
            <label>
              Fuso horário
              <select name="timezone" defaultValue={profile.timezone}>
                {TIMEZONES.map((tz) => (
                  <option key={tz} value={tz}>{tz}</option>
                ))}
              </select>
            </label>
          </div>
        </fieldset>
        {!canEdit && <p>Seu papel permite apenas consultar estes dados.</p>}
      </Panel>
      <Panel title="Contatos do condomínio">
        {contacts.length === 0 && <p>Nenhum contato cadastrado.</p>}
        <div className="table">
          {contacts.map((contact, index) => (
            <div className="table-row" key={index}>
              <span>
                <strong>{contact.name}</strong>
                <small>{contact.role}</small>
              </span>
              <span>
                {contact.email}
                <small>{contact.phone}</small>
              </span>
              {canEdit && (
                <span className="row-actions">
                  <button
                    type="button"
                    className="secondary"
                    onClick={() => setContacts(contacts.filter((_, i) => i !== index))}
                  >
                    Remover
                  </button>
                </span>
              )}
            </div>
          ))}
        </div>
        {canEdit && <ContactAdd onAdd={(contact) => setContacts([...contacts, contact])} />}
      </Panel>
      {canEdit && (
        <Panel>
          <button type="submit" disabled={update.isPending}>
            {update.isPending ? "Salvando…" : "Salvar alterações"}
          </button>
          {saved && !update.isPending && <p className="success">Perfil atualizado.</p>}
          <InlineError error={update.error} />
        </Panel>
      )}
    </form>
  );
}

function ContactAdd({ onAdd }: { onAdd: (contact: TenantContact) => void }) {
  const [name, setName] = useState("");
  const [role, setRole] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  return (
    <div className="form-grid" style={{ marginTop: 16 }}>
      <label>
        Nome do contato
        <input value={name} onChange={(e) => setName(e.target.value)} />
      </label>
      <label>
        Função
        <input value={role} onChange={(e) => setRole(e.target.value)} placeholder="Síndico, zelador…" />
      </label>
      <label>
        E-mail
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
      </label>
      <label>
        Telefone
        <input value={phone} onChange={(e) => setPhone(e.target.value)} />
      </label>
      <div className="form-actions">
        <button
          type="button"
          className="secondary"
          disabled={!name.trim()}
          onClick={() => {
            onAdd({ name: name.trim(), role: role.trim(), email: email.trim(), phone: phone.trim() });
            setName(""); setRole(""); setEmail(""); setPhone("");
          }}
        >
          Adicionar contato
        </button>
      </div>
    </div>
  );
}
